var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, html, LitElement } from "lit";
import { property } from "lit/decorators.js";
import { reset } from "../styles.js";
import { styles } from "../tabs/styles.js";
/**
 * Tab panel component that holds content for individual tabs.
 * Each tab panel should have a name that matches a corresponding tab.
 *
 * @parent w-tabs
 */
export class WarpTabPanel extends LitElement {
    static { this.styles = [
        reset,
        styles,
        css `
			:host {
				display: block;
			}
			/* Stories and legacy markup may set [hidden] on inactive panels.
       * Visibility is now controlled internally via active state, so neutralize it. */
			:host([hidden]) {
				display: block !important;
			}
			.panel-content {
				display: none;
			}
			.panel-content[data-active] {
				display: block;
			}
		`,
    ]; }
    constructor() {
        super();
        this._internals = this.attachInternals();
    }
    /**
     * Whether this panel is active (visible).
     * Set by parent w-tabs via the _parentActive property.
     *
     * @internal
     */
    set active(value) {
        this._ownActive = value;
    }
    /** @internal */
    get active() {
        return this._parentActive ?? this._ownActive ?? false;
    }
    connectedCallback() {
        super.connectedCallback();
        // Use ElementInternals for ARIA to avoid hydration mismatches
        this._internals.role = "tabpanel";
        this.syncA11yState();
    }
    updated() {
        // Sync aria-labelledby to ElementInternals (no DOM attribute needed)
        // Property name is ariaLabelledBy (camelCase per ARIA IDL spec)
        // Type assertion needed as TypeScript DOM types may not include this on ElementInternals
        this.syncA11yState();
    }
    syncA11yState() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const internals = this._internals;
        internals.ariaLabelledBy = this._parentAriaLabelledBy || null;
        // Keep inactive panels out of the accessibility tree without mutating host attributes.
        internals.ariaHidden = this.active ? "false" : "true";
    }
    render() {
        return html `<div class="panel-content" ?data-active=${this.active}>
			<slot></slot>
		</div>`;
    }
}
__decorate([
    property({ attribute: "id", reflect: true })
], WarpTabPanel.prototype, "id", void 0);
__decorate([
    property({ type: Boolean })
], WarpTabPanel.prototype, "active", null);
__decorate([
    property({ attribute: false })
], WarpTabPanel.prototype, "_parentActive", void 0);
__decorate([
    property({ attribute: false })
], WarpTabPanel.prototype, "_parentAriaLabelledBy", void 0);
if (!customElements.get("w-tab-panel")) {
    customElements.define("w-tab-panel", WarpTabPanel);
}
//# sourceMappingURL=tab-panel.js.map